import React from 'react';
import { string, shape } from 'prop-types';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import Arrow from 'TMComponents/Icons/Arrow';
import { spacing } from 'TMDesignSystem/spacing';
import borderRadius from 'TMDesignSystem/borders';
import imageProps from 'TMPropTypes/imageProps';

const Container = styled.div`
    background: ${({ theme }) => theme.colorSystem.UIElements[5]};
    border: 2px solid ${({ theme }) => theme.colorSystem.UIElements[9]};
    border-radius: ${borderRadius.medium};
    padding: ${spacing.mediumLarge};
`;

const Title = styled.h3`
    font-size: ${spacing.mediumLarge};
    font-weight: 700;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin: 0 0 ${spacing.medium} 0;

    span {
        color: ${({ theme }) => theme.colorSystem.secondary[3]};
    }
`;

const ContactText = styled.p`
    font-size: ${spacing.medium};
    font-weight: 400;
    color: ${({ theme }) => theme.colorSystem.UIText[1]};
    margin: 0 0 ${spacing.mediumLarge} 0;
`;

const EmailLink = styled.a`
    color: ${({ theme }) => theme.colorSystem.primary[4]};
    text-decoration: none;
    font-weight: 700;

    &:hover {
        text-decoration: underline;
    }
`;

const ContactPerson = styled.div`
    padding: ${spacing.mediumLarge};
    background: ${({ theme }) => theme.colorSystem.primary[3]};
    border-radius: ${borderRadius.small};
`;

const Avatar = styled.div`
    width: ${spacing.xxxLarge};
    height: ${spacing.xxxLarge};
    border-radius: ${borderRadius.full};
    overflow: hidden;
    margin-bottom: ${spacing.medium};

    .gatsby-image-wrapper {
        width: 100%;
        height: 100%;
    }
`;

const ContactName = styled.span`
    font-size: ${spacing.medium};
    font-weight: 700;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
`;

const ContactTitle = styled.span`
    font-size: ${spacing.medium};
    font-weight: 400;
    color: ${({ theme }) => theme.colorSystem.UIText[1]};
`;

const NameTitleWrapper = styled.div`
    margin-bottom: ${spacing.small};
`;

const ContactEmail = styled.div`
    font-size: ${spacing.medium};
    margin-bottom: ${spacing.medium};
`;

const MoreLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: ${spacing.small};
    font-size: ${spacing.medium};
    font-weight: 700;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    text-decoration: none;

    svg {
        width: 17px;
        height: 20px;
    }

    &:hover {
        text-decoration: underline;
    }
`;

const PressEnquiries = ({
    title,
    generalEmail,
    contactName,
    contactTitle,
    contactEmail,
    contactImage,
    contactLinkText,
    contactLinkUrl
}) => {
    const formatTitle = (text) => {
        if (!text) return text;
        if (text.endsWith('.')) {
            return (
                <>
                    {text.slice(0, -1)}
                    <span>.</span>
                </>
            );
        }
        return text;
    };

    return (
        <Container>
            <Title>{formatTitle(title)}</Title>
            <ContactText>
                Contact{' '}
                <EmailLink href={`mailto:${generalEmail}`}>
                    {generalEmail}
                </EmailLink>
            </ContactText>
            {contactName && (
                <ContactPerson>
                    {contactImage && (
                        <Avatar>
                            <GatsbyImage
                                image={contactImage}
                                alt={contactName}
                            />
                        </Avatar>
                    )}
                    <NameTitleWrapper>
                        <ContactName>{contactName}</ContactName>
                        {contactTitle && (
                            <>
                                {' - '}
                                <ContactTitle>{contactTitle}</ContactTitle>
                            </>
                        )}
                    </NameTitleWrapper>
                    {contactEmail && (
                        <ContactEmail>
                            <EmailLink href={`mailto:${contactEmail}`}>
                                {contactEmail}
                            </EmailLink>
                        </ContactEmail>
                    )}
                    {contactLinkText && contactLinkUrl && (
                        <MoreLink href={contactLinkUrl}>
                            {contactLinkText}
                            <Arrow color="currentColor" />
                        </MoreLink>
                    )}
                </ContactPerson>
            )}
        </Container>
    );
};

PressEnquiries.propTypes = {
    title: string.isRequired,
    generalEmail: string.isRequired,
    contactName: string,
    contactTitle: string,
    contactEmail: string,
    contactImage: shape(imageProps),
    contactLinkText: string,
    contactLinkUrl: string
};

PressEnquiries.defaultProps = {
    contactName: null,
    contactTitle: null,
    contactEmail: null,
    contactImage: null,
    contactLinkText: null,
    contactLinkUrl: null
};

export default PressEnquiries;
